// Client admin per /api/admin/{api-keys,webhooks,retention,settings/kiosk,export}.
// Sprint 7.

import { request } from './_request';
import type {
  ApiKeyCreateInput,
  ApiKeyListItem,
  WebhookCreateInput,
  WebhookListItem,
  WebhookUpdateInput,
  KioskSettingsUpdateInput,
  RetentionExecutionRowDto,
  WebhookDeliveryRow,
} from '@audiq/shared';

// ----- API KEYS -----
export async function listApiKeys(): Promise<ApiKeyListItem[]> {
  const res = await request<{ ok: true; data: ApiKeyListItem[] }>('/api/admin/api-keys');
  return res.data;
}

export interface ApiKeyCreatedResponse {
  api_key: ApiKeyListItem;
  raw_key: string;
}
export async function createApiKey(input: ApiKeyCreateInput): Promise<ApiKeyCreatedResponse> {
  return request<{ ok: true } & ApiKeyCreatedResponse>('/api/admin/api-keys', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function revokeApiKey(id: string): Promise<void> {
  await request(`/api/admin/api-keys/${id}/revoke`, { method: 'POST' });
}

export async function getApiKeyUsage(id: string): Promise<{
  api_key: ApiKeyListItem;
  usage: { period_days: number; audit_events_count: number };
}> {
  return request(`/api/admin/api-keys/${id}/usage`);
}

// ----- WEBHOOKS -----
export async function listWebhooks(): Promise<WebhookListItem[]> {
  const res = await request<{ ok: true; data: WebhookListItem[] }>('/api/admin/webhooks');
  return res.data;
}

export async function createWebhook(input: WebhookCreateInput): Promise<{
  webhook: WebhookListItem;
  raw_secret: string;
}> {
  return request('/api/admin/webhooks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateWebhook(id: string, input: WebhookUpdateInput): Promise<void> {
  await request(`/api/admin/webhooks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function deleteWebhook(id: string): Promise<void> {
  await request(`/api/admin/webhooks/${id}`, { method: 'DELETE' });
}

export async function testWebhook(id: string): Promise<{
  delivery_id: string;
  result: { status: string; http_status: number | null; response_body_excerpt: string | null };
}> {
  return request(`/api/admin/webhooks/${id}/test`, { method: 'POST' });
}

export async function listWebhookDeliveries(id: string): Promise<WebhookDeliveryRow[]> {
  const res = await request<{ ok: true; data: WebhookDeliveryRow[] }>(
    `/api/admin/webhooks/${id}/deliveries`,
  );
  return res.data;
}

export async function retryWebhookDelivery(webhookId: string, deliveryId: string): Promise<void> {
  await request(`/api/admin/webhooks/${webhookId}/deliveries/${deliveryId}/retry`, {
    method: 'POST',
  });
}

// ----- KIOSK -----
export interface KioskSettings {
  kiosk_mode_enabled: boolean;
  pin_configured: boolean;
  kiosk_idle_reset_seconds: number;
  kiosk_welcome_message: string | null;
}

export async function getKioskSettings(): Promise<KioskSettings> {
  const res = await request<{ ok: true; kiosk: KioskSettings }>('/api/admin/settings/kiosk');
  return res.kiosk;
}

export async function updateKioskSettings(input: KioskSettingsUpdateInput): Promise<void> {
  await request('/api/admin/settings/kiosk', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

// ----- RETENTION -----
export async function listRetentionExecutions(): Promise<RetentionExecutionRowDto[]> {
  const res = await request<{ ok: true; data: RetentionExecutionRowDto[] }>(
    '/api/admin/retention/executions',
  );
  return res.data;
}

export async function runRetentionNow(): Promise<unknown> {
  return request('/api/admin/retention/run-now', { method: 'POST' });
}

// ----- BULK EXPORT -----
export const bulkExportFullUrl = '/api/admin/export/full';
